import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useApiQuery } from "../../../services/api";
import { useFirebaseUser } from "../../user/hooks/useFirebaseUser";
import { isRoomContentPath } from "../utils/roomContentPath";

/**
 * Side-effect hook: handles room error redirects.
 * Called once in RoomRoutes.
 */
export function useRoomGuard(roomId: string | null): void {
  const history = useHistory();
  const location = useLocation();
  const { isAuthenticatedFirebase: isAuthenticated } = useFirebaseUser();
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    roomId !== null && isAuthenticated ? { param: { id: roomId } } : false
  );

  // A shared article or playlist is not the room, and a reader who is not in
  // the room may still read it: that is the axiom in `room-access.ts`, and the
  // 403 below was the last place still contradicting it. It sent a signed-in
  // non-member to `/discovery` off a link a colleague had sent them, while an
  // anonymous reader of the SAME link was served the page.
  //
  // `AppRoutes` renders these two paths from the public room card instead, so
  // there is nothing here to rescue them from.
  const isOnSharedContent = isRoomContentPath(location.pathname);

  // Handle room fetch errors: 401 handled globally, 403 -> discovery, other -> log + discovery
  const roomFetchError = roomQuery.state === "error" ? roomQuery.message : null;
  const roomErrorStatus = roomQuery.state === "error" ? roomQuery.status : null;

  useEffect(() => {
    if (roomFetchError === null) return;
    // 401 is handled globally by apiClient.onRequestError
    if (roomErrorStatus === 401) return;
    if (isOnSharedContent) return;
    if (roomErrorStatus === 403) {
      history.replace("/discovery");
    } else {
      console.error("Room fetch error:", roomFetchError);
      history.replace("/discovery");
    }
  }, [roomFetchError, roomErrorStatus, history, isOnSharedContent]);
}
